import { createContext, useContext, useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTH_DISABLED, DEV_USER } from "@/lib/constants";

const AuthContext = createContext(null);
const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000; // 2 Stunden

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in

  useEffect(() => {
    if (AUTH_DISABLED) {
      localStorage.removeItem("sadcr_token");
      setUser(DEV_USER);
      return;
    }
    const token = localStorage.getItem("sadcr_token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("sadcr_token");
        setUser(false);
      });
  }, []);

  const login = async (name, password) => {
    const { data } = await api.post("/auth/login", { name, password });
    localStorage.setItem("sadcr_token", data.token);
    setUser(data.user);
    return data;
  };

  const createAccount = async (payload) => {
    const { data } = await api.post("/auth/accounts", payload);
    return data;
  };

  const changePassword = async (old_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { old_password, new_password });
    return data;
  };

  const logout = async () => {
    if (AUTH_DISABLED) {
      setUser(DEV_USER);
      return;
    }
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem("sadcr_token");
    setUser(false);
  };

  // Heartbeat (Präsenz) alle 60s, solange eingeloggt
  useEffect(() => {
    if (AUTH_DISABLED || !user || user === DEV_USER) return;
    const beat = () => api.post("/auth/heartbeat").catch(() => {});
    beat();
    const id = setInterval(beat, 60000);
    return () => clearInterval(id);
  }, [user]);

  // Auto-Logout nach 2h ohne Eingabe (Seitenwechsel zählt nicht)
  useEffect(() => {
    if (AUTH_DISABLED || !user || user === DEV_USER) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast.warning("Automatisch abgemeldet wegen Inaktivität (2 Std.)");
        logout();
      }, IDLE_LIMIT_MS);
    };
    const events = ["mousedown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, createAccount, changePassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
